import { v4 as uuidv4 } from 'uuid'
import { omitBy, isEmpty, isNumber, isBoolean } from 'lodash'
import { generatePageTitle } from './templates'
import type { PatternConfig, PatternDefinition } from './routerConfigTypes'
import type { FilterExpression } from '@wix/wix-data-client-common-standalone'

export const generate = ({
  routeDefinitionBase = {},
  pageRole = uuidv4(),
  pattern,
  collectionId,
  seoV2,
}: {
  routeDefinitionBase?: Partial<
    Omit<PatternDefinition, 'config'> & { config: Partial<PatternConfig> }
  >
  pageRole?: string
  pattern: string
  collectionId: string
  seoV2: boolean
}): PatternDefinition => ({
  ...(seoV2 ? {} : { seoMetaTags: {} }),
  ...routeDefinitionBase,
  pageRole,
  title: generatePageTitle(collectionId, pattern),
  config: {
    ...routeDefinitionBase.config,
    collection: collectionId,
    lowercase: true,
    seoV2,
  },
})

export const getPageTitle = (patternDef: PatternDefinition): string =>
  patternDef.title

export const setPageTitle = (
  patternDef: PatternDefinition,
  newTitle: string,
): PatternDefinition => ({
  ...patternDef,
  title: newTitle,
})

export const getCollection = (patternDef: PatternDefinition): string =>
  patternDef.config.collection

export const getFilterCount = (patternDef: PatternDefinition): number =>
  ((patternDef.config.filter?.$and as FilterExpression[]) ?? []).length

export const getSeoMetaTags = (patternDef: PatternDefinition) =>
  patternDef.seoMetaTags || {}

export const setSeoMetaTags = (
  patternDef: PatternDefinition,
  newSeoMetaTags: PatternDefinition['seoMetaTags'],
): PatternDefinition => ({
  ...patternDef,
  seoMetaTags: newSeoMetaTags,
})

export const mergeSeoMetaTags = (
  patternDef: PatternDefinition,
  newSeoMetaTags: PatternDefinition['seoMetaTags'],
): PatternDefinition =>
  setSeoMetaTags(patternDef, {
    ...getSeoMetaTags(patternDef),
    ...newSeoMetaTags,
  })

export const setIndexing = (
  patternDef: PatternDefinition,
  isIndexable: boolean,
): PatternDefinition =>
  setSeoMetaTags(patternDef, {
    ...getSeoMetaTags(patternDef),
    robots: isIndexable ? 'index' : 'noindex',
  })

export const getDatasetConfig = (
  patternDef: PatternDefinition,
): PatternConfig => patternDef.config

const omitByPredicate = (val: unknown): boolean =>
  isEmpty(val) && !isNumber(val) && !isBoolean(val)

export const setDatasetConfig = (
  patternDef: PatternDefinition,
  newDatasetConfig: PatternConfig,
): PatternDefinition => ({
  ...patternDef,

  config: {
    collection: patternDef.config.collection,
    ...omitBy(
      {
        ...patternDef.config,
        ...newDatasetConfig,
      },
      omitByPredicate,
    ),
  },
})

export const removeSeoMetaTags = (
  patternDef: PatternDefinition,
): PatternDefinition => {
  const { seoMetaTags, ...rest } = patternDef
  return rest
}
